/**
 * 채팅 세션 + 개인 테마 API 클라이언트
 */
import apiClient from './client';

export interface ChatSessionItem {
  sessionId: string;
  chatId: string;
  adminId: string;
  workflowId: string;
  userContent: string;
  assistantContent?: string;
  startDateTime: string;
  endDateTime?: string;
  modelId: string;
  themeMode: string;
  status: string;
  resultTime?: string;
  inputToken?: number;
  outputToken?: number;
  totalToken?: number;
  sources?: Array<{ documentName: string; score: number; excerpt?: string; url?: string }>;
  traceContent?: Array<Record<string, unknown>>;
  title?: string;
  ttl?: number;
  messageId?: string;  // nanoid — 표시용 고유 ID
}

export interface ChatTheme {
  primaryColor?: string;
  backgroundColor?: string;
  botName?: string;
  welcomeMessage?: string;
  quickPrompts?: string[];
  userBubbleColor?: string;
  userTextColor?: string;
  botBubbleColor?: string;
  botTextColor?: string;
  botIconColor?: string;     // 챗봇 아이콘 배경 색상
  botIconFgColor?: string;   // 챗봇 아이콘(svg) 색상
}

export const chatSessionApi = {
  /** 내 세션 목록 */
  async listSessions(workflowId?: string): Promise<ChatSessionItem[]> {
    const url = workflowId
      ? `/api/chat-sessions?workflowId=${workflowId}`
      : '/api/chat-sessions';
    const res = await apiClient.get<{ success: boolean; data: ChatSessionItem[] }>(url);
    return res.data.data;
  },

  /** 세션 상세 (전체 메시지) */
  async getSession(sessionId: string): Promise<ChatSessionItem[]> {
    const res = await apiClient.get<{ success: boolean; data: ChatSessionItem[] }>(
      `/api/chat-sessions/${sessionId}`
    );
    return res.data.data;
  },

  /** 사용자 메시지 저장 */
  async saveUserMessage(params: {
    sessionId?: string;
    chatId?: string;
    workflowId: string;
    userContent: string;
    startDateTime?: string;
    modelId?: string;
    themeMode?: string;
    messageId?: string;
  }): Promise<{ sessionId: string; chatId: string }> {
    const res = await apiClient.post<{ success: boolean; data: { sessionId: string; chatId: string } }>(
      '/api/chat-sessions/user',
      { ...params, startDateTime: params.startDateTime || new Date().toISOString() }
    );
    return res.data.data;
  },

  /** AI 응답 저장 */
  async saveAssistantMessage(params: {
    sessionId: string;
    chatId: string;
    assistantContent: string;
    endDateTime?: string;
    inputToken?: number;
    outputToken?: number;
    totalToken?: number;
    resultTime?: string;
    status?: string;
    messageId?: string;
    sources?: Array<{ documentName: string; score: number; excerpt?: string; url?: string }>;
    traceContent?: Array<Record<string, unknown>>;
  }): Promise<void> {
    await apiClient.put('/api/chat-sessions/assistant', {
      ...params,
      endDateTime: params.endDateTime || new Date().toISOString(),
    });
  },

  /** 세션 삭제 */
  async deleteSession(sessionId: string): Promise<void> {
    await apiClient.delete(`/api/chat-sessions/${sessionId}`);
  },

  /** 세션 제목 변경 */
  async renameSession(sessionId: string, title: string): Promise<void> {
    await apiClient.put(`/api/chat-sessions/${sessionId}/rename`, { title });
  },

  /** 워크플로우별 테마 조회 */
  async getTheme(workflowId: string): Promise<ChatTheme | null> {
    const res = await apiClient.get<{ success: boolean; data: ChatTheme | null }>(`/api/user-themes/${workflowId}`);
    return res.data.data;
  },

  /** 워크플로우별 테마 저장 */
  async saveTheme(workflowId: string, theme: ChatTheme): Promise<ChatTheme> {
    const res = await apiClient.put<{ success: boolean; data: ChatTheme }>(`/api/user-themes/${workflowId}`, { theme });
    return res.data.data;
  },
};

export default chatSessionApi;
